import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, ArrowRight, Star, Grid, List, Map } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';
import { SEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Calculate reading time
const calculateReadingTime = (content) => {
  const wordsPerMinute = 200;
  const words = content?.split(/\s+/).length || 0;
  return Math.max(1, Math.ceil(words / wordsPerMinute));
};

export const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeYear, setActiveYear] = useState('all');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('map'); // Default: map view
  const { theme } = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsRes, catsRes] = await Promise.all([
          fetch(`${API}/blog`),
          fetch(`${API}/admin/blog-categories`)
        ]);
        const postsData = await postsRes.json();
        setPosts(postsData);
        
        if (catsRes.ok) {
          const catsData = await catsRes.json();
          setCategories(catsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get available years from posts
  const availableYears = useMemo(() => {
    const years = posts
      .map(p => p.date ? new Date(p.date).getFullYear() : null)
      .filter(y => y !== null);
    return ['all', ...Array.from(new Set(years)).sort((a, b) => b - a)];
  }, [posts]);

  // Get unique categories from posts if API doesn't return them
  const availableCategories = useMemo(() => {
    if (categories.length > 0) {
      return ['all', ...categories.map(c => c.name)];
    }
    const cats = posts.map(p => p.category).filter(Boolean);
    return ['all', ...Array.from(new Set(cats))];
  }, [posts, categories]);

  // Filter posts by category and year
  const filteredPosts = useMemo(() => {
    return posts.filter(p => {
      const matchesCategory = activeCategory === 'all' || p.category === activeCategory;
      const postYear = p.date ? new Date(p.date).getFullYear() : null;
      const matchesYear = activeYear === 'all' || postYear === activeYear;
      return matchesCategory && matchesYear;
    });
  }, [posts, activeCategory, activeYear]);

  // Posts with coordinates for map
  const postsWithCoords = useMemo(() => 
    filteredPosts.filter(p => p.coordinates && p.coordinates.length === 2),
    [filteredPosts]
  );

  // Map center calculation
  const mapCenter = useMemo(() => {
    if (postsWithCoords.length === 0) return [43.2630, -2.9350]; // Default: Bilbao
    const lats = postsWithCoords.map(p => p.coordinates[1]);
    const lngs = postsWithCoords.map(p => p.coordinates[0]);
    return [
      lats.reduce((a, b) => a + b, 0) / lats.length,
      lngs.reduce((a, b) => a + b, 0) / lngs.length
    ];
  }, [postsWithCoords]);

  const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  const mapClass = theme === 'dark' ? 'leaflet-tiles-dark' : '';

  const getAverageRating = (post) => {
    if (!post.rating_count || post.rating_count === 0) return null;
    return (post.rating_sum / post.rating_count).toFixed(1);
  };

  return (
    <main className="min-h-screen bg-background pt-24 pb-16" data-testid="blog-list-page">
      <SEO 
        title="Blog - Diario de un Fotógrafo"
        description="Reflexiones, consejos y experiencias de un fotógrafo profesional. Descubre historias detrás de las fotos, técnicas y el día a día en el mundo de la fotografía."
        url="https://jonquintano.com/blog"
      />
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-sm uppercase tracking-widest text-muted-foreground block mb-4">
            Blog
          </span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mb-4">
            Diario de un Fotógrafo
          </h1>
          <p className="text-lg text-muted-foreground/80 font-serif italic mb-2">
            Un Álbum Personal
          </p>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Más allá de los trabajos por encargo
          </p>
        </motion.div>

        {/* Filters & View Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8"
        >
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {availableCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 text-xs uppercase tracking-wider transition-all ${
                  activeCategory === cat
                    ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                data-testid={`filter-${cat}`}
              >
                {cat === 'all' ? 'Todos' : cat}
              </button>
            ))}
          </div>

          {/* Year Filter & View Mode */}
          <div className="flex items-center gap-4">
            {/* Year Dropdown */}
            {availableYears.length > 2 && (
              <select
                value={activeYear}
                onChange={(e) => setActiveYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                className="px-3 py-2 text-xs bg-muted border-0 focus:ring-1 focus:ring-foreground"
                data-testid="year-filter"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>
                    {year === 'all' ? 'Todos los años' : year}
                  </option>
                ))}
              </select>
            )}

            {/* View Mode Toggle */}
            <div className="flex border border-border">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-foreground text-background' : 'hover:bg-muted'}`}
                title="Vista en cuadrícula"
                data-testid="view-grid"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-foreground text-background' : 'hover:bg-muted'}`}
                title="Vista en lista"
                data-testid="view-list"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 transition-colors ${viewMode === 'map' ? 'bg-foreground text-background' : 'hover:bg-muted'}`}
                title="Vista en mapa"
                data-testid="view-map"
              >
                <Map className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Results count */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-sm text-muted-foreground mb-6"
        >
          {filteredPosts.length} {filteredPosts.length === 1 ? 'publicación' : 'publicaciones'}
          {activeCategory !== 'all' && ` en "${activeCategory}"`}
          {activeYear !== 'all' && ` de ${activeYear}`}
        </motion.p>

        {/* Main Content */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 space-y-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="flex flex-col sm:flex-row gap-0">
                    <div className="sm:w-72 h-48 bg-muted" />
                    <div className="flex-1 p-6 space-y-3">
                      <div className="h-4 bg-muted w-1/3" />
                      <div className="h-6 bg-muted w-3/4" />
                      <div className="h-4 bg-muted w-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-2 h-[500px] bg-muted animate-pulse" />
          </div>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.post_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="group"
                    data-testid={`post-card-${post.post_id}`}
                  >
                    <Link to={`/blog/${post.post_id}`}>
                      <div className="relative aspect-[4/3] overflow-hidden mb-4 bg-muted">
                        {post.thumbnail ? (
                          <img
                            src={post.thumbnail}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            Sin imagen
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="uppercase tracking-wider">{post.category}</span>
                          {post.date && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(post.date).toLocaleDateString('es-ES', { 
                                  day: 'numeric', month: 'short', year: 'numeric' 
                                })}
                              </span>
                            </>
                          )}
                          {getAverageRating(post) && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1 text-yellow-500">
                                <Star className="w-3 h-3 fill-current" />
                                {getAverageRating(post)}
                              </span>
                            </>
                          )}
                        </div>
                        
                        <h2 className="font-serif text-xl group-hover:text-muted-foreground transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        
                        {post.excerpt && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {post.excerpt}
                          </p>
                        )}

                        {post.location && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {post.location}
                          </p>
                        )}
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </motion.div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.post_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="group"
                    data-testid={`post-list-${post.post_id}`}
                  >
                    <Link to={`/blog/${post.post_id}`} className="flex gap-6 items-start">
                      <div className="relative w-48 h-32 flex-shrink-0 overflow-hidden bg-muted">
                        {post.thumbnail ? (
                          <img
                            src={post.thumbnail}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                            Sin imagen
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="uppercase tracking-wider">{post.category}</span>
                          {post.date && (
                            <>
                              <span>•</span>
                              <span>{new Date(post.date).toLocaleDateString('es-ES', { 
                                day: 'numeric', month: 'long', year: 'numeric' 
                              })}</span>
                            </>
                          )}
                          {post.content && (
                            <>
                              <span>•</span>
                              <span>{calculateReadingTime(post.content)} min de lectura</span>
                            </>
                          )}
                          {getAverageRating(post) && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1 text-yellow-500">
                                <Star className="w-3 h-3 fill-current" />
                                {getAverageRating(post)} ({post.rating_count})
                              </span>
                            </>
                          )}
                        </div>
                        
                        <h2 className="font-serif text-2xl group-hover:text-muted-foreground transition-colors">
                          {post.title}
                        </h2>
                        
                        {post.excerpt && (
                          <p className="text-muted-foreground line-clamp-2">
                            {post.excerpt}
                          </p>
                        )}

                        {post.location && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {post.location}
                          </p>
                        )}
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </motion.div>
            )}

            {/* Map View (Default) */}
            {viewMode === 'map' && (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Posts List - Left Side (3 columns) */}
                <div className="lg:col-span-3 order-2 lg:order-1">
                  <div className="space-y-6">
                    {filteredPosts.map((post, index) => (
                      <motion.article
                        key={post.post_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link 
                          to={`/blog/${post.post_id}`}
                          className="group block bg-card border border-border hover:border-foreground/30 transition-all duration-300"
                          data-testid={`blog-post-${post.post_id}`}
                        >
                          <div className="flex flex-col sm:flex-row">
                            {/* Thumbnail - Large */}
                            <div className="sm:w-64 md:w-72 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                              {post.thumbnail ? (
                                <img
                                  src={post.thumbnail}
                                  alt={post.title}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                              ) : (
                                <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground">
                                  Sin imagen
                                </div>
                              )}
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 p-5 sm:p-6 flex flex-col justify-center">
                              {/* Meta info */}
                              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-3">
                                <span className="uppercase tracking-wider font-medium">{post.category}</span>
                                {post.date && (
                                  <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {new Date(post.date).toLocaleDateString('es-ES', { 
                                        day: 'numeric', month: 'long', year: 'numeric' 
                                      })}
                                    </span>
                                  </>
                                )}
                                {getAverageRating(post) && (
                                  <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1 text-yellow-500">
                                      <Star className="w-3 h-3 fill-current" />
                                      {getAverageRating(post)}
                                    </span>
                                  </>
                                )}
                              </div>
                              
                              {/* Title - Large */}
                              <h2 className="font-serif text-xl md:text-2xl mb-3 group-hover:text-muted-foreground transition-colors leading-tight">
                                {post.title}
                              </h2>
                              
                              {/* Excerpt */}
                              {post.excerpt && (
                                <p className="text-muted-foreground line-clamp-2 text-sm md:text-base leading-relaxed mb-3">
                                  {post.excerpt}
                                </p>
                              )}

                              {/* Location */}
                              {post.location && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mb-3">
                                  <MapPin className="w-3 h-3" />
                                  {post.location}
                                </p>
                              )}
                              
                              {/* Read more */}
                              <span className="text-xs uppercase tracking-wider text-muted-foreground group-hover:text-foreground transition-colors flex items-center gap-2">
                                Leer más <ArrowRight className="w-3 h-3" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      </motion.article>
                    ))}
                  </div>
                </div>

                {/* Map - Right Side (2 columns), sticky */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="lg:col-span-2 order-1 lg:order-2"
                >
                  <div className="lg:sticky lg:top-24 h-[350px] lg:h-[600px] border border-border overflow-hidden">
                    {postsWithCoords.length > 0 ? (
                      <MapContainer
                        center={mapCenter}
                        zoom={6}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom={false}
                        className={mapClass}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url={tileUrl}
                        />
                        {postsWithCoords.map(post => (
                          <Marker
                            key={post.post_id}
                            position={[post.coordinates[1], post.coordinates[0]]}
                            icon={customIcon}
                          >
                            <Popup>
                              <Link to={`/blog/${post.post_id}`} className="block w-52 no-underline text-inherit">
                                {post.thumbnail && (
                                  <img src={post.thumbnail} alt={post.title} className="w-full h-28 object-cover mb-2" />
                                )}
                                <p className="font-medium text-sm mb-1">{post.title}</p>
                                <p className="text-xs text-muted-foreground mb-2">{post.location}</p>
                                <span className="text-xs text-primary font-medium">Ver entrada →</span>
                              </Link>
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center bg-muted text-muted-foreground text-center p-4">
                        <div>
                          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No hay posts con ubicación en este filtro</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            )}

            {/* Empty State */}
            {filteredPosts.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <p className="text-muted-foreground mb-4">
                  No hay publicaciones que coincidan con los filtros seleccionados.
                </p>
                <Button
                  variant="outline"
                  onClick={() => { setActiveCategory('all'); setActiveYear('all'); }}
                  className="rounded-none"
                >
                  Mostrar todas las publicaciones
                </Button>
              </motion.div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default BlogList;
